import { useContext, useEffect } from 'react';
import { DispatchEvent, State } from '../../../../../../library/Core/SmartTypes';
import { SmartContext } from '../../../../../../library/Core/SmartContext';
import PageBuilder from '../../../../../../library/Builders/PageBuilder/PageBuilder';
import EditWorkProfileFormLayout from './EditWorkProfileFormLayout';
import ProfileMarkerService from './ProfileMakerService';
import WorkProfileConfig from './FormConfiguration/WorkProfileConfig.json';

const EditWorkProfileModal = ({ userdata,workProfileProps }: any) => {
    const { state, dispatch } = useContext(SmartContext);
    const { saveWorkRelatedMarker, fetchUserProfileMarkers } = ProfileMarkerService();

    const handleSave = async (
    event: React.SyntheticEvent<HTMLButtonElement>,
    state: State,
    dispatch: (dispatchEvent: DispatchEvent) => void
) => {
    event.preventDefault();

    const visibleControl  = state?.data?.visibleControl;
    const workProfileData = state?.data?.workProfile || {};

    // ─── 1. Extract numeric markers ───────────────────────────────────────────
    const numericMarkers = Object.entries(state?.data?.marker?.workProfile || {})
        .filter(([k]) => /^\d+$/.test(k))
        .map(([, v]) => v as any);

    const existingMarker = numericMarkers.find((m: any) => m.markerField === visibleControl) || {
        markerField:   visibleControl,
        value:         '',
        remark:        '',
        markerEnabled: '0',
    };

    const markerEnabled = workProfileData[`${visibleControl}`] ?? existingMarker.markerEnabled;

    // ─── 2. Generic value resolver ────────────────────────────────────────────
   const resolveValue = (field: string): string => {
        if (!markerEnabled || markerEnabled == '0' || markerEnabled == 0) return '';

        const selectedId  = workProfileData[`${field}_value`];
        const manualEntry = workProfileData[`${field}_value_Other`];

        // ✅ convert boolean to 1/0
        const normalise = (val: any): string => {
            if (val === true  || val === 'true')  return '1';
            if (val === false || val === 'false') return '0';
            if (val != null) return String(val).trim();
            return '';
        };

        const manualEntryStr = normalise(manualEntry);
        const selectedIdStr  = normalise(selectedId);

        if (manualEntryStr) return manualEntryStr;
        if (selectedIdStr)  return selectedIdStr;

        return existingMarker.value != null ? String(existingMarker.value) : '';
    };

    // ─── 5. Build payload ───────────────────
    const formValue = resolveValue(visibleControl);

    // ─── 3. Validation: work_experience ───────────────────────────────────────
    if (visibleControl === 'work_experience') {
        const numericValue = parseFloat(formValue);
        if (markerEnabled == '1' && (isNaN(numericValue) || numericValue < 15)) {
            alert('Please enter a value greater than or equal to 15 for Work Experience.');
            return;
        }
        if (markerEnabled == '0' && numericValue >= 15) {
            alert('Value for Work Experience should not be greater than or equal to 15 if the checkbox is not checked.');
            return;
        }
    }

    // ─── 4. Validation: comment required if value changed ─────────────────────
    const propertiesToValidate = [
        'work_experience',
        'is_leadership_role_in_large_company',
        'is_leadership_role_in_500_company',
        'is_leadership_role_in_sme_company',
        'is_leadership_role_in_a_listed_company',
        'is_growing_startup_founder',
        'is_mid_senior_role_in_fortune_company',
        'director_or_board_member',
    ];

    if (visibleControl && propertiesToValidate.includes(visibleControl)) {
        const syncData      = state?.data?.syncData?.[0] || {};
        const syncDataValue = String(syncData[visibleControl]);
        const compareValue  = visibleControl === 'work_experience' ? formValue : String(markerEnabled);

        if (syncDataValue !== compareValue) {
            const remark = workProfileData[`${visibleControl}Comment`] ?? existingMarker.remark ?? '';
            if (!remark?.trim()) {
                alert('Please enter a comment because the value has changed.');
                return;
            }
        }
    }

    // ─── 5. Build payload ─────────────────────────────────────────────────────
    // ✅ If marker disabled — remark also null
    const remark = (!markerEnabled || markerEnabled == '0' || markerEnabled == 0)
    ? ''
    : workProfileData[`${visibleControl}Comment`] ?? existingMarker.remark ?? '';

    const payload = {
        ...existingMarker,
        markerField:   visibleControl,
        markerType:    existingMarker.markerType || 'workProfile',
        markerEnabled: markerEnabled,
        value:         formValue,
        remark,
    };

    // ─── 6. Save ──────────────────────────────────────────────────────────────
    await saveWorkRelatedMarker(payload, state?.internal?.model?.id);
    await fetchUserProfileMarkers(state?.internal?.model?.id);
    dispatch({ type: 'TOGGLE_MODAL_VISIBILITY', payload: { modalName: 'showWorkProfileModal' } });
};

    useEffect(() => {
        dispatch({ type: 'ADD_ACTIONS', payload: { ...state.actions, SAVE: handleSave } });
    }, []);

    return (
        <SmartContext.Provider value={{ state, dispatch }}>
            <EditWorkProfileFormLayout>
                {!state.flags.isDataLoading && <PageBuilder />}
            </EditWorkProfileFormLayout>
        </SmartContext.Provider>
    );
};

export default EditWorkProfileModal;
